// src/pages/VerifyEmailPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { auth } from '../firebase/config';
import { claimVerifiedAgreements } from '../firebase/services';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon } from '../components/Icon';

export default function VerifyEmailPage() {
  const { user, resendVerification, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const refreshed = await refreshUser();
        if (refreshed?.emailVerified) {
          await auth.currentUser.getIdToken(true);
          claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] auto-poll:', err));
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('[VerifyEmail] poll refresh failed:', err);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [refreshUser, navigate]);

  const handleResend = async () => {
    try {
      await resendVerification();
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch {
      // Rate limited or other error
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckError('');
    try {
      const refreshedUser = await refreshUser();
      if (refreshedUser?.emailVerified) {
        await auth.currentUser.getIdToken(true);
        claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] manual check:', err));
        navigate('/dashboard');
      } else {
        setCheckError(t('verify.notVerified'));
      }
    } catch (err) {
      console.error('[VerifyEmail] manual check failed:', err);
      setCheckError(t('verify.notVerified'));
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // If user is already verified (e.g. navigated here manually), redirect
  if (user?.emailVerified) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 48, paddingBottom: 60 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><CircleIcon name="mail" color="var(--primary)" size={52} iconSize={26} /></div>
          <h1 style={{ fontSize: '1.4rem', marginBottom: 8 }}>{t('verify.title')}</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: 1.6 }}>
            {t('verify.sentTo')}<br />
            <strong>{user?.email}</strong>
          </p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.84rem', color: 'var(--muted)', marginBottom: 16, lineHeight: 1.6 }}>
            {t('verify.clickLink')}
          </p>
          <button className="btn btn-primary" onClick={handleCheck} disabled={checking}>
            {checking ? t('verify.checking') : t('verify.verified')}
          </button>
          {checkError && <p style={{ color: 'var(--declined)', fontSize: '0.82rem', marginTop: 8 }}>{checkError}</p>}
          <button className="btn btn-secondary" onClick={handleResend} disabled={sent}>
            {sent ? <><SvgIcon name="check" size={14} color="var(--confirmed)" /> {t('verify.emailSent')}</> : t('verify.resend')}
          </button>
        </div>

        <div style={{
          background: 'var(--primary-light)', border: '1px solid var(--primary)',
          borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginTop: 12,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <SvgIcon name="info" size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: '0.78rem', color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>
            {t('verify.spamNotice')}
          </p>
        </div>

        <button
          className="btn btn-secondary"
          onClick={handleLogout}
          style={{ marginTop: 8 }}
        >
          {t('verify.logOut')}
        </button>
      </div>
    </div>
  );
}
